import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, getSessionId } from "@/lib/api";
import { track, EVENTS } from "@/lib/analytics";
import {
    Compass, ArrowRight, ArrowLeft, Check, Sparkles, Heart, Home,
    AlertCircle, Users, ClipboardList,
} from "lucide-react";
import { toast } from "sonner";

/* -------------------------------------------------------------------------- */
/*  Intake — 5-step personalization wizard. Drives the dashboard recs.        */
/* -------------------------------------------------------------------------- */

const FAST_QUIZ = [
    { stage: 2, q: "Forgets familiar names or where they put things — only they notice." },
    { stage: 3, q: "Others have started noticing — trouble at work, getting lost in new places, repeating questions." },
    { stage: 4, q: "Difficulty with complex tasks: finances, planning meals, traveling alone." },
    { stage: 5, q: "Needs help choosing clothes, or forgets major details like their address." },
    { stage: 6, q: "Needs hands-on help with bathing, dressing, or toileting." },
    { stage: 7, q: "Speech is limited to a few words or hard to understand." },
    { stage: 7, q: "Has lost the ability to walk, sit up, or hold up their head without help." },
];

const RELATIONSHIPS = [
    { v: "spouse", label: "Spouse / partner" },
    { v: "child", label: "Adult child" },
    { v: "parent", label: "Parent of someone with younger-onset" },
    { v: "sibling", label: "Sibling" },
    { v: "grandchild", label: "Grandchild" },
    { v: "friend", label: "Friend or neighbor" },
    { v: "professional", label: "Professional caregiver" },
];

const LIVING_SITUATIONS = [
    { v: "with_me", label: "Lives with me", desc: "Same home, same household" },
    { v: "alone", label: "Lives alone", desc: "Independent or semi-independent" },
    { v: "with_other_family", label: "Lives with other family", desc: "Different household than mine" },
    { v: "assisted_living", label: "Assisted living", desc: "Community / facility" },
    { v: "memory_care", label: "Memory care", desc: "Specialized dementia community" },
    { v: "nursing_facility", label: "Skilled nursing facility", desc: "Long-term care setting" },
];

const MAIN_CONCERNS = [
    { v: "safety", label: "Safety at home" },
    { v: "behavior", label: "Behavior changes" },
    { v: "medications", label: "Medications" },
    { v: "memory_loss", label: "Memory loss progression" },
    { v: "communication", label: "Communication is harder" },
    { v: "physical_decline", label: "Physical decline / mobility" },
    { v: "finances", label: "Finances / paying for care" },
    { v: "legal", label: "Legal / Power of Attorney" },
    { v: "caregiving_load", label: "I’m overwhelmed" },
    { v: "family_friction", label: "Family disagreements" },
    { v: "end_of_life", label: "End-of-life planning" },
    { v: "isolation", label: "Loneliness / isolation" },
];

const SAFETY = [
    { v: "wandering", label: "Wandering / getting lost" },
    { v: "falls", label: "Falls or balance issues" },
    { v: "driving", label: "Driving still happening" },
    { v: "stove_cooking", label: "Stove / cooking risks" },
    { v: "medications", label: "Medication mix-ups" },
    { v: "firearms", label: "Firearms in the home" },
    { v: "leaving_home", label: "Leaves the home unsupervised" },
    { v: "stranger_calls", label: "Scams / unwanted calls" },
    { v: "none", label: "None right now" },
];

const SUPPORT_LEVELS = [
    { v: "alone", label: "Mostly on my own", desc: "No one else helps regularly" },
    { v: "some_family", label: "Some family helps", desc: "Occasional help, not consistent" },
    { v: "strong_family", label: "Strong family team", desc: "Multiple people share the load" },
    { v: "paid_help", label: "We have paid help", desc: "Aide, day program, etc." },
    { v: "mixed", label: "A mix of family + paid", desc: "Best of both" },
];

const STEPS = [
    { id: 0, label: "About them", Icon: Home },
    { id: 1, label: "Stage check", Icon: ClipboardList },
    { id: 2, label: "Your concerns", Icon: AlertCircle },
    { id: 3, label: "Safety", Icon: Sparkles },
    { id: 4, label: "You as caregiver", Icon: Heart },
];

export default function Intake() {
    const sid = getSessionId();
    const navigate = useNavigate();
    const [step, setStep] = useState(0);
    const [data, setData] = useState({
        relationship_to_patient: "",
        living_situation: "",
        fast_answers: FAST_QUIZ.map((q) => ({ stage: q.stage, yes: null })),
        // Pre-diagnosis opt-out. When true, caregiver has told us they're
        // noticing memory changes but haven't been through evaluation.
        // We bypass the FAST screener and route them into the "noticing"
        // stage of the roadmap so they still get value immediately.
        predx: false,
        main_concerns: [],
        safety_concerns: [],
        support_level: "",
        burnout_score: 3,   // 0-10 slider
        hours_per_week: 10,
        country: "US",
        us_state: "",
    });
    const [saving, setSaving] = useState(false);

    // onboarding_started — once per mount, only when starting fresh (GA4
    // funnel step; completion fires onboarding_completed on submit below).
    useEffect(() => {
        track(EVENTS.ONBOARDING_STARTED, {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Load existing intake (if any) so users can edit
    useEffect(() => {
        let active = true;
        api.get(`/intake/${sid}`).then(r => {
            if (!active || !r.data) return;
            const d = r.data;
            // Hydrate, falling back to existing structure if shape differs
            const fast = Array.isArray(d.fast_answers) && d.fast_answers.length === FAST_QUIZ.length
                ? d.fast_answers
                : FAST_QUIZ.map((q) => ({ stage: q.stage, yes: null }));
            setData({
                relationship_to_patient: d.relationship_to_patient || "",
                living_situation: d.living_situation || "",
                fast_answers: fast,
                predx: Boolean(d.predx),
                main_concerns: d.main_concerns || [],
                safety_concerns: d.safety_concerns || [],
                support_level: d.support_level || "",
                burnout_score: typeof d.burnout_score === "number" ? d.burnout_score : 3,
                hours_per_week: typeof d.hours_per_week === "number" ? d.hours_per_week : 10,
                country: d.country || "US",
                us_state: d.us_state || "",
            });
        }).catch(() => { });
        return () => { active = false; };
    }, [sid]);

    const stepValid = useMemo(() => {
        switch (step) {
            case 0: return !!data.relationship_to_patient && !!data.living_situation;
            // Step 1 (stage check) is satisfied EITHER by answering every
            // FAST yes/no OR by choosing the "no diagnosis yet" opt-out.
            // See StepFast for the UX affordance.
            case 1: return data.predx || data.fast_answers.every(a => a.yes !== null);
            case 2: return data.main_concerns.length > 0;
            case 3: return data.safety_concerns.length > 0;
            case 4: return !!data.support_level;
            default: return false;
        }
    }, [step, data]);

    const toggleArr = (key, v) =>
        setData(d => {
            const arr = d[key].includes(v) ? d[key].filter(x => x !== v) : [...d[key], v];
            // If "none" is selected for safety, clear others; if any other selected, clear "none"
            if (key === "safety_concerns") {
                if (v === "none" && !d[key].includes("none")) return { ...d, [key]: ["none"] };
                if (v !== "none") return { ...d, [key]: arr.filter(x => x !== "none") };
            }
            return { ...d, [key]: arr };
        });

    const setFast = (i, yes) =>
        setData(d => {
            const next = [...d.fast_answers];
            next[i] = { ...next[i], yes };
            // Answering any yes/no is mutually exclusive with the
            // pre-diagnosis opt-out — clear predx so the two paths
            // can't disagree.
            return { ...d, fast_answers: next, predx: false };
        });

    // Toggle the "we don't have a diagnosis yet" opt-out. Enabling it
    // clears any partial FAST answers so the backend receives a clean
    // predx-only payload. Disabling it just clears the flag and lets
    // the yes/no questions become the source of truth again.
    const setPredx = (on) =>
        setData(d => ({
            ...d,
            predx: !!on,
            fast_answers: on
                ? FAST_QUIZ.map((q) => ({ stage: q.stage, yes: null }))
                : d.fast_answers,
        }));

    const next = () => setStep(s => Math.min(s + 1, STEPS.length - 1));
    const prev = () => setStep(s => Math.max(s - 1, 0));

    const submit = async () => {
        setSaving(true);
        try {
            await api.put(`/intake/${sid}`, data);
            track(EVENTS.ONBOARDING_COMPLETED, { steps: STEPS.length });
            navigate("/dashboard?plan=ready");
        } catch {
            toast.error("Couldn’t save. Please try again.");
        } finally {
            setSaving(false);
        }
    };

    const progressPct = Math.round(((step + 1) / STEPS.length) * 100);

    return (
        <div data-clarity-mask="true" className="min-h-screen bg-cream">
            {/* Top bar */}
            <header className="bg-cream/80 backdrop-blur-sm border-b border-[#E2E8F0]/60 sticky top-0 z-30">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-3">
                    <span className="w-10 h-10 rounded-2xl bg-teal text-white flex items-center justify-center">
                        <Compass strokeWidth={1.8} size={20} />
                    </span>
                    <div className="leading-tight">
                        <div className="font-heading font-semibold text-[#1F2624]">Help us walk this with you</div>
                        <div className="text-xs text-[#4C5551] -mt-0.5">Step {step + 1} of {STEPS.length} · about 3 minutes</div>
                    </div>
                </div>
                {/* Progress bar */}
                <div className="h-1.5 bg-cream-dark">
                    <div className="h-full bg-teal transition-all duration-500" style={{ width: `${progressPct}%` }} />
                </div>
            </header>

            <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 animate-fade-in-up">
                {/* Stepper pills */}
                <div className="flex md:flex-row flex-wrap items-center gap-1.5 md:gap-2 mb-6 md:mb-8" data-testid="intake-stepper">
                    {STEPS.map((s, i) => {
                        const done = i < step;
                        const active = i === step;
                        return (
                            <React.Fragment key={s.id}>
                                <div className={`flex items-center gap-1.5 px-2.5 py-1 md:px-3 md:py-1.5 rounded-2xl text-xs md:text-sm transition-colors ${active
                                    ? "bg-teal text-white"
                                    : done ? "bg-teal-soft text-teal-dark"
                                        : "bg-cream-dark text-[#4C5551]"
                                    }`}>
                                    {done ? <Check size={12} strokeWidth={2.4} /> : <s.Icon size={12} strokeWidth={1.6} />}
                                    <span className="font-medium hidden sm:inline">{s.label}</span>
                                    <span className="font-medium sm:hidden">{i + 1}</span>
                                </div>
                                {i < STEPS.length - 1 && <div className="hidden md:block flex-1 h-px bg-[#E2E8F0]" />}
                            </React.Fragment>
                        );
                    })}
                </div>

                {/* Step content */}
                <section className="ml-card p-6 md:p-10" data-testid={`intake-step-${step}`}>
                    {step === 0 && <StepRelationship data={data} setData={setData} />}
                    {step === 1 && <StepFast data={data} setFast={setFast} setPredx={setPredx} />}
                    {step === 2 && <StepConcerns data={data} toggleArr={toggleArr} />}
                    {step === 3 && <StepSafety data={data} toggleArr={toggleArr} />}
                    {step === 4 && <StepCaregiver data={data} setData={setData} />}
                </section>

                {/* Footer nav */}
                <div className="mt-6 flex items-center justify-between gap-3">
                    <button
                        onClick={prev}
                        disabled={step === 0}
                        data-testid="intake-back"
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-2xl text-sm font-medium text-[#4C5551] hover:bg-cream-dark disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                        <ArrowLeft size={16} strokeWidth={1.6} /> Back
                    </button>

                    {step < STEPS.length - 1 ? (
                        <button
                            onClick={next}
                            disabled={!stepValid}
                            data-testid="intake-next"
                            className="ml-btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                            Continue <ArrowRight size={18} strokeWidth={1.8} />
                        </button>
                    ) : (
                        <button
                            onClick={submit}
                            disabled={!stepValid || saving}
                            data-testid="intake-submit"
                            className="ml-btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                            {saving ? "Personalizing…" : <>See my personalized plan <ArrowRight size={18} strokeWidth={1.8} /></>}
                        </button>
                    )}
                </div>

                <p className="mt-6 text-xs text-[#6E7A74] text-center">
                    Your answers stay private and only personalize what you see inside Memory Lane.
                </p>
            </main>
        </div>
    );
}

/* ---------- Step components ---------- */

function StepRelationship({ data, setData }) {
    return (
        <>
            <h2 className="text-2xl md:text-3xl font-heading font-semibold tracking-tight">First, tell us a bit about your loved one</h2>
            <p className="mt-2 text-[#4C5551]">No medical jargon needed. We just want to know enough to walk this with you, not at you.</p>

            <div className="mt-7">
                <p className="text-xs uppercase tracking-wider font-semibold text-[#4C5551]">Your relationship to them</p>
                <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {RELATIONSHIPS.map(r => (
                        <button key={r.v} type="button"
                            data-testid={`intake-relationship-${r.v}`}
                            onClick={() => setData(d => ({ ...d, relationship_to_patient: r.v }))}
                            className={`text-left px-4 py-3 rounded-2xl text-sm font-medium border transition-all ${data.relationship_to_patient === r.v
                                ? "bg-teal text-white border-teal"
                                : "bg-white border-[#E2E8F0] text-[#1F2624] hover:bg-cream"
                                }`}>
                            {r.label}
                        </button>
                    ))}
                </div>
            </div>

            <div className="mt-7">
                <p className="text-xs uppercase tracking-wider font-semibold text-[#4C5551]">Where do they currently live?</p>
                <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {LIVING_SITUATIONS.map(l => (
                        <button key={l.v} type="button"
                            data-testid={`intake-living-${l.v}`}
                            onClick={() => setData(d => ({ ...d, living_situation: l.v }))}
                            className={`text-left px-4 py-3 rounded-2xl text-sm border transition-all ${data.living_situation === l.v
                                ? "bg-teal text-white border-teal"
                                : "bg-white border-[#E2E8F0] text-[#1F2624] hover:bg-cream"
                                }`}>
                            <div className="font-medium">{l.label}</div>
                            <div className={`text-xs mt-0.5 ${data.living_situation === l.v ? "text-white/80" : "text-[#4C5551]"}`}>{l.desc}</div>
                        </button>
                    ))}
                </div>
            </div>

            {/* Country + (if US) state. Tells us where to point local resources. */}
            <div className="mt-7">
                <h3 className="font-medium text-[#1F2624]">Where are you based?</h3>
                <p className="text-xs text-[#4C5551] mt-1">Helps us point you to local resources, programs, and laws.</p>
                <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                    <label className="block">
                        <span className="text-xs uppercase tracking-wider font-semibold text-[#4C5551]">Country</span>
                        <select
                            value={data.country}
                            onChange={(e) => setData(d => ({ ...d, country: e.target.value, us_state: e.target.value === "US" ? d.us_state : "" }))}
                            data-testid="intake-country"
                            className="mt-1 w-full px-3 py-2 rounded-xl text-sm border border-[#E2E8F0] bg-white text-[#1F2624] focus:border-teal focus:ring-1 focus:ring-teal outline-none"
                        >
                            <option value="US">United States</option>
                            <option value="CA">Canada</option>
                            <option value="MX">Mexico</option>
                            <option value="GB">United Kingdom</option>
                            <option value="IE">Ireland</option>
                            <option value="ES">Spain (España)</option>
                            <option value="AU">Australia</option>
                            <option value="NZ">New Zealand</option>
                            <option value="IN">India</option>
                            <option value="OTHER">Somewhere else</option>
                        </select>
                    </label>
                    {data.country === "US" && (
                        <label className="block">
                            <span className="text-xs uppercase tracking-wider font-semibold text-[#4C5551]">State</span>
                            <select
                                value={data.us_state}
                                onChange={(e) => setData(d => ({ ...d, us_state: e.target.value }))}
                                data-testid="intake-state"
                                className="mt-1 w-full px-3 py-2 rounded-xl text-sm border border-[#E2E8F0] bg-white text-[#1F2624] focus:border-teal focus:ring-1 focus:ring-teal outline-none"
                            >
                                <option value="">Select state…</option>
                                {["AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "DC", "FL", "GA", "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD", "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ", "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC", "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY"].map(s => <option key={s} value={s}>{s}</option>)}
                            </select>
                        </label>
                    )}
                </div>
            </div>
        </>
    );
}

function StepFast({ data, setFast, setPredx }) {
    const predx = !!data.predx;
    return (
        <>
            <h2 className="text-2xl md:text-3xl font-heading font-semibold tracking-tight">Where are they on the journey?</h2>
            <p className="mt-2 text-[#4C5551]">Seven quick yes/no questions. Trust your gut — you know them better than any test does.</p>

            {/* Pre-diagnosis opt-out — sits ABOVE the FAST screener so
                caregivers who can't confidently answer the yes/no items
                still get to value without being forced to pick a stage
                they don't know. Toggling it clears any partial answers
                so the two paths never disagree. The backend force-writes
                estimated_stage = 0 when this flag is on, which lands
                the family in the "Noticing changes" bucket of the roadmap. */}
            <button
                type="button"
                data-testid="intake-predx-card"
                onClick={() => setPredx(!predx)}
                aria-pressed={predx}
                className={`mt-6 w-full text-left rounded-2xl border-2 p-5 md:p-6 transition-all ${predx
                    ? "border-teal bg-teal-soft"
                    : "border-teal-light/60 bg-cream-light hover:bg-teal-soft/40 hover:border-teal-light"
                    }`}
            >
                <div className="flex items-start gap-3">
                    <span className={`shrink-0 w-11 h-11 rounded-2xl flex items-center justify-center ${predx ? "bg-white text-teal-dark" : "bg-white text-teal-dark"}`}>
                        <Sparkles size={18} strokeWidth={1.8} />
                    </span>
                    <div className="flex-1 min-w-0">
                        <p className="text-[10px] uppercase tracking-wider font-semibold text-teal-dark">
                            Don&apos;t have a diagnosis yet?
                        </p>
                        <p className="mt-1 text-lg md:text-xl font-heading text-[#1F2624] leading-snug">
                            We&apos;re noticing memory changes but don&apos;t have a diagnosis yet.
                        </p>
                        <p className="mt-2 text-sm text-[#4C5551] leading-relaxed max-w-xl">
                            Pick this if the questions below feel like a guess. We&apos;ll skip the staging quiz,
                            start you at &ldquo;Noticing changes&rdquo; on the roadmap, and route you to the right
                            first steps — a primary-care baseline visit, a neurology or geriatrician referral,
                            and how to rule out treatable causes like B12, thyroid, sleep apnea, or medication side effects.
                        </p>
                    </div>
                    <span
                        className={`shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center ${predx ? "bg-teal border-teal text-white" : "border-teal/50 bg-white"}`}
                        data-testid="intake-predx-indicator"
                    >
                        {predx && <Check size={14} strokeWidth={3} />}
                    </span>
                </div>
            </button>

            <div className={`mt-6 transition-opacity ${predx ? "opacity-40 pointer-events-none" : "opacity-100"}`} aria-hidden={predx}>
                <p className="text-xs uppercase tracking-wider font-semibold text-[#6E7A74] mb-3">
                    Or — answer the 7 quick yes/no questions:
                </p>
                <ol className="space-y-3">
                    {FAST_QUIZ.map((q, i) => (
                        <li key={i} className="ml-card p-4 bg-cream-dark !border-cream-dark" data-testid={`intake-fast-${i}`}>
                            <p className="text-[#1F2624]">
                                <span className="font-semibold text-teal-dark mr-2">{i + 1}.</span>
                                {q.q}
                            </p>
                            <div className="flex gap-2 mt-3">
                                {[
                                    { v: true, label: "Yes" },
                                    { v: false, label: "No / not yet" },
                                ].map(opt => (
                                    <button key={String(opt.v)} type="button"
                                        data-testid={`intake-fast-${i}-${opt.v ? "yes" : "no"}`}
                                        onClick={() => setFast(i, opt.v)}
                                        disabled={predx}
                                        className={`flex-1 sm:flex-none sm:min-w-[120px] py-2 px-4 rounded-2xl text-sm font-medium border ${data.fast_answers[i]?.yes === opt.v
                                            ? (opt.v ? "bg-teal text-white border-teal" : "bg-[#4C5551] text-white border-[#4C5551]")
                                            : "bg-white border-[#E2E8F0] text-[#4C5551] hover:bg-cream"
                                            }`}>
                                        {opt.label}
                                    </button>
                                ))}
                            </div>
                        </li>
                    ))}
                </ol>
            </div>
        </>
    );
}

function StepConcerns({ data, toggleArr }) {
    return (
        <>
            <h2 className="text-2xl md:text-3xl font-heading font-semibold tracking-tight">What&apos;s on your mind right now?</h2>
            <p className="mt-2 text-[#4C5551]">Pick everything that&apos;s weighing on you. There are no wrong answers — and we won&apos;t share this with anyone.</p>

            <div className="mt-7 flex flex-wrap gap-2" data-testid="intake-concerns-pills">
                {MAIN_CONCERNS.map(c => {
                    const on = data.main_concerns.includes(c.v);
                    return (
                        <button key={c.v} type="button"
                            data-testid={`intake-concern-${c.v}`}
                            onClick={() => toggleArr("main_concerns", c.v)}
                            className={`px-4 py-2 rounded-2xl text-sm font-medium border transition-all ${on
                                ? "bg-teal text-white border-teal"
                                : "bg-white border-[#E2E8F0] text-[#1F2624] hover:bg-cream"
                                }`}>
                            {on ? <Check size={14} strokeWidth={2.2} className="inline mr-1.5" /> : null}
                            {c.label}
                        </button>
                    );
                })}
            </div>
            <p className="mt-4 text-xs text-[#6E7A74]">Selected: {data.main_concerns.length}</p>
        </>
    );
}

function StepSafety({ data, toggleArr }) {
    return (
        <>
            <h2 className="text-2xl md:text-3xl font-heading font-semibold tracking-tight">Any safety concerns?</h2>
            <p className="mt-2 text-[#4C5551]">If any of these apply, we&apos;ll prioritize them in your plan. Check all that fit — or &quot;None right now&quot;.</p>

            <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="intake-safety">
                {SAFETY.map(s => {
                    const on = data.safety_concerns.includes(s.v);
                    const isNone = s.v === "none";
                    return (
                        <button key={s.v} type="button"
                            data-testid={`intake-safety-${s.v}`}
                            onClick={() => toggleArr("safety_concerns", s.v)}
                            className={`text-left px-4 py-3 rounded-2xl text-sm font-medium border transition-all ${on
                                ? (isNone
                                    ? "bg-[#DDE5D3] text-[#4F6B3D] border-[#DDE5D3]"
                                    : "bg-coral-light text-coral-dark border-coral-light")
                                : "bg-white border-[#E2E8F0] text-[#1F2624] hover:bg-cream"
                                }`}>
                            {on ? <Check size={14} strokeWidth={2.2} className="inline mr-1.5" /> : null}
                            {s.label}
                        </button>
                    );
                })}
            </div>
        </>
    );
}

function StepCaregiver({ data, setData }) {
    return (
        <>
            <h2 className="text-2xl md:text-3xl font-heading font-semibold tracking-tight">Now you. How&apos;s it going, really?</h2>
            <p className="mt-2 text-[#4C5551]">Caregiver wellbeing changes what we recommend just as much as the stage. There are no wrong answers here.</p>

            <div className="mt-7">
                <p className="text-xs uppercase tracking-wider font-semibold text-[#4C5551]">Your support network</p>
                <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {SUPPORT_LEVELS.map(l => (
                        <button key={l.v} type="button"
                            data-testid={`intake-support-${l.v}`}
                            onClick={() => setData(d => ({ ...d, support_level: l.v }))}
                            className={`text-left px-4 py-3 rounded-2xl text-sm border transition-all ${data.support_level === l.v
                                ? "bg-teal text-white border-teal"
                                : "bg-white border-[#E2E8F0] text-[#1F2624] hover:bg-cream"
                                }`}>
                            <div className="font-medium">{l.label}</div>
                            <div className={`text-xs mt-0.5 ${data.support_level === l.v ? "text-white/80" : "text-[#4C5551]"}`}>{l.desc}</div>
                        </button>
                    ))}
                </div>
            </div>

            <div className="mt-7">
                <p className="text-xs uppercase tracking-wider font-semibold text-[#4C5551]">How burned out are you feeling? (0 = fine, 10 = ready to break)</p>
                <div className="mt-3 flex items-center gap-3">
                    <input type="range" min="0" max="10" step="1"
                        data-testid="intake-burnout"
                        value={data.burnout_score}
                        onChange={e => setData(d => ({ ...d, burnout_score: Number(e.target.value) }))}
                        className="flex-1 accent-teal" />
                    <span className={`min-w-[44px] text-center font-heading font-semibold ${data.burnout_score >= 7 ? "text-coral-dark"
                        : data.burnout_score >= 4 ? "text-yellow-dark"
                            : "text-teal-dark"
                        }`}>
                        {data.burnout_score} / 10
                    </span>
                </div>
                <p className="mt-1 text-xs text-[#6E7A74]">
                    {data.burnout_score >= 7 ? "We hear you. Respite will be a priority."
                        : data.burnout_score >= 4 ? "Some signs of strain — we’ll build in breaks."
                            : "Holding steady. We’ll focus on what’s next."}
                </p>
            </div>

            <div className="mt-7">
                <label htmlFor="hpw" className="text-xs uppercase tracking-wider font-semibold text-[#4C5551]">
                    Roughly how many hours per week are you caregiving?
                </label>
                <input id="hpw" type="number" min="0" max="168"
                    data-testid="intake-hours"
                    value={data.hours_per_week}
                    onChange={e => setData(d => ({ ...d, hours_per_week: Number(e.target.value) || 0 }))}
                    className="ml-input mt-2 max-w-[200px]" />
                <p className="mt-1 text-xs text-[#6E7A74]">A rough guess is fine.</p>
            </div>
        </>
    );
}
